import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseButton, useT } from '@repo/common';
import { ReactComponent as ArrowLeftIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-left.svg';

import {
  STEP_FIELDS,
  STEP_KEYS,
  STEP_TITLES,
  findStepOfField,
  validateStep,
} from './jobPostValidation';

/**
 * 공고 등록 모바일 위저드.
 */
const JobPostMobileWizard = ({
  isEditMode,
  stepNodes,
  agreement,
  textValues,
  errors,
  setErrors,
  failedValidationField,
  onFailedFieldHandled,
  canPublish,
  isBusy,
  onSaveDraft,
  onPublish,
  toast = null,
}) => {
  const t = useT();
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [pendingScrollField, setPendingScrollField] = useState(null);

  const stepKey = STEP_KEYS[stepIndex];
  const isLastStep = stepIndex === STEP_KEYS.length - 1;
  const nextStepKey = isLastStep ? null : STEP_KEYS[stepIndex + 1];

  const title = useMemo(() => {
    const [en, ko] = STEP_TITLES[stepKey];
    return t(en, ko);
  }, [stepKey, t]);

  useEffect(() => {
    if (pendingScrollField) return;
    window.scrollTo(0, 0);
  }, [stepIndex, pendingScrollField]);

  useEffect(() => {
    if (!pendingScrollField) return;

    const field = document.querySelector(
      `[data-validation-field="${pendingScrollField}"]`,
    );
    field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (!field?.readOnly) field?.focus?.({ preventScroll: true });

    setPendingScrollField(null);
  }, [pendingScrollField, stepIndex]);

  useEffect(() => {
    if (!failedValidationField) return;

    const targetStep = findStepOfField(failedValidationField);
    const targetIndex = STEP_KEYS.indexOf(targetStep);
    if (targetIndex >= 0) setStepIndex(targetIndex);
    setPendingScrollField(failedValidationField);

    onFailedFieldHandled?.();
  }, [failedValidationField, onFailedFieldHandled]);

  const goBack = () => {
    if (stepIndex > 0) {
      setStepIndex((prev) => prev - 1);
      return;
    }
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate('/jobs', { replace: true });
    }
  };

  const goNext = () => {
    const stepErrors = validateStep(stepKey, textValues, t);

    const carriedErrors = Object.fromEntries(
      Object.entries(errors).filter(
        ([field]) => !STEP_FIELDS[stepKey].includes(field),
      ),
    );
    setErrors({ ...carriedErrors, ...stepErrors });

    const firstInvalidField = Object.keys(stepErrors)[0];
    if (firstInvalidField) {
      setPendingScrollField(firstInvalidField);
      return;
    }

    setStepIndex((prev) => prev + 1);
  };

  const nextLabel = nextStepKey
    ? `${t('Next', '다음')}: ${t(
        STEP_TITLES[nextStepKey][0],
        STEP_TITLES[nextStepKey][1],
      )}`
    : t('Publish Job', '공고 게시');

  return (
    <div className='flex min-h-screen w-full flex-col bg-grayscale-gray00'>
      <div className='sticky top-0 z-20 shrink-0 bg-grayscale-gray00'>
        <header className='flex items-center justify-between gap-2 px-4 py-3'>
          <button
            type='button'
            onClick={goBack}
            className='flex items-center gap-1 typo-b14Strong text-text-primary'
          >
            <ArrowLeftIcon className='size-5 shrink-0' />
            {isEditMode
              ? t('Edit Job', '공고 수정')
              : t('Create Job', '공고 등록')}
          </button>

          <BaseButton
            type='text'
            size='s'
            hideIcon
            isDisabled={isBusy}
            onClick={onSaveDraft}
          >
            {t('Save draft', '임시저장')}
          </BaseButton>
        </header>

        <div
          role='progressbar'
          aria-valuemin={1}
          aria-valuemax={STEP_KEYS.length}
          aria-valuenow={stepIndex + 1}
          className='h-1 w-full bg-grayscale-gray02'
        >
          <div
            className='h-full bg-green-deep02 transition-[width] duration-300 ease-out motion-reduce:transition-none'
            style={{ width: `${((stepIndex + 1) / STEP_KEYS.length) * 100}%` }}
          />
        </div>
      </div>

      <div className='flex-1 px-[30px] pb-[200px]'>
        <h1 className='pt-4 mb-[30px] typo-b16Strong tabletH:typo-title2 text-text-primary'>
          <div className='py-2'>{title}</div>
        </h1>

        {stepNodes[stepKey]}

        {isLastStep && agreement}
      </div>

      {/* 하단 바 */}
      <div className='fixed bottom-0 left-0 right-0 z-10 flex gap-2 border-t border-grayscale-gray02 bg-grayscale-gray00 px-4 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-3'>
        <BaseButton
          type='text'
          size='l'
          hideIcon
          className='shrink-0'
          isDisabled={isBusy}
          onClick={goBack}
        >
          {t('Back', '이전')}
        </BaseButton>

        <BaseButton
          type='primary'
          size='l'
          hideIcon
          className='flex-1'
          isDisabled={isBusy || (isLastStep && !canPublish)}
          onClick={isLastStep ? onPublish : goNext}
        >
          {nextLabel}
        </BaseButton>
      </div>

      {toast}
    </div>
  );
};

export default JobPostMobileWizard;
